/* Source-faithful finish paint. Product geometry, fit, fonts, options and cart handlers stay with their existing renderer. */
(() => {
  if (customElements.get('rr-photo-finish-materials')) return;
  const NS = 'http://www.w3.org/2000/svg';
  const textures = new Map();
  const sourceMasks = new Map();
  let nextId = 0;
  const set = (el, name, value, priority = '') => {
    if (el.style.getPropertyValue(name) !== value || el.style.getPropertyPriority(name) !== priority) el.style.setProperty(name, value, priority);
  };
  const white = value => /^rgba?\(\s*25[0-5][, ]+25[0-5][, ]+25[0-5](?:\s*[,/]\s*(?:1|100%))?\s*\)$/.test(value || '');
  function texture(source) {
    const key = source.url + '|' + source.crop.join(',');
    if (!textures.has(key)) textures.set(key, new Promise(resolve => {
      const img = new Image(); img.crossOrigin = 'anonymous';
      img.onload = () => {
        try {
          const [x,y,w,h] = source.crop;
          const crop = document.createElement('canvas'); crop.width = w; crop.height = h;
          crop.getContext('2d').drawImage(img,x,y,w,h,0,0,w,h);
          const mirror = document.createElement('canvas'); mirror.width = w*2; mirror.height = h*2;
          const ctx = mirror.getContext('2d');
          for (const [fx,fy] of [[0,0],[1,0],[0,1],[1,1]]) {
            ctx.save(); ctx.translate(fx ? w*2 : 0,fy ? h*2 : 0); ctx.scale(fx ? -1 : 1,fy ? -1 : 1); ctx.drawImage(crop,0,0); ctx.restore();
          }
          resolve({crop:crop.toDataURL(),tile:mirror.toDataURL(),w,h});
        } catch (_) { resolve(null); }
      };
      img.onerror = () => resolve(null); img.src = source.url;
    }));
    return textures.get(key);
  }
  function sourceMask(url,mode) {
    const key=url+'|'+mode;
    if(!sourceMasks.has(key))sourceMasks.set(key,new Promise(resolve=>{
      const img=new Image();img.crossOrigin='anonymous';
      img.onload=()=>{try{
        const c=document.createElement('canvas');c.width=img.naturalWidth;c.height=img.naturalHeight;
        const ctx=c.getContext('2d',{willReadFrequently:true});ctx.drawImage(img,0,0);
        const pixels=ctx.getImageData(0,0,c.width,c.height),d=pixels.data;
        for(let i=0;i<d.length;i+=4){
          // Source identity is whitelisted after visual review; reject unexpected visible color.
          if(d[i+3]>0&&Math.max(d[i],d[i+1],d[i+2])-Math.min(d[i],d[i+1],d[i+2])>12){resolve(null);return;}
          if(mode!=='alpha')d[i+3]=Math.round(d[i+3]*(1-(.2126*d[i]+.7152*d[i+1]+.0722*d[i+2])/255));
          d[i]=d[i+1]=d[i+2]=255;
        }
        ctx.putImageData(pixels,0,0);resolve(c);
      }catch(_){resolve(null);}};
      img.onerror=()=>resolve(null);img.src=url;
    }));
    return sourceMasks.get(key);
  }
  class RRPhotoFinishMaterials extends HTMLElement {
    connectedCallback() {
      if (this.started) return;
      try { this.config = JSON.parse(this.querySelector('[data-rr-photo-config]').textContent); } catch (_) { return; }
      this.started = true; this.a = this.config.adapter; this.sources = this.config.sources;
      this.scope = this.closest('product-info') || this.closest('section');
      if (!this.scope) return;
      // D13 has large source photographs. Load its native picker samples only
      // when they approach the viewport; all other product renderers stay as-is.
      if (this.a.family === 'approved_d13_reference' && 'IntersectionObserver' in window) {
        this.sampleObserver = new IntersectionObserver(entries => {
          entries.forEach(entry => {
            if (!entry.isIntersecting) return;
            this.sampleObserver.unobserve(entry.target);
            this.loadSample(entry.target, entry.target.dataset.rrPhotoSample);
          });
        }, {rootMargin: '100px'});
      }
      this.paintId = `rr-photo-pattern-${++nextId}`;
      this.original = new Map(); this.imageStates = new Map(); this.request = (this.request || 0) + 1; this.pending = false;
      this.schedule = () => { if (!this.pending) { this.pending = true; requestAnimationFrame(() => { this.pending=false; if(this.isConnected) this.refresh(); }); } };
      this.scope.addEventListener('change',this.schedule);
      this.scope.addEventListener('input',this.schedule);
      this.scope.addEventListener('click',this.schedule);
      this.observer = new MutationObserver(records => {
        if (records.some(r => r.type==='childList' || ['data-selected-finish','data-finish','hidden','viewBox','src'].includes(r.attributeName))) this.schedule();
      });
      this.observer.observe(this.scope,{subtree:true,childList:true,attributes:true,attributeFilter:['data-selected-finish','data-finish','hidden','viewBox','src']});
      this.resize = new ResizeObserver(this.schedule); this.resize.observe(this.scope);
      document.fonts?.ready.then(this.schedule); this.refresh();
    }
    picker() { return this.scope.querySelector('variant-selects,variant-radios'); }
    optionControls(name) {
      const picker = this.picker(); if(!picker) return [];
      return [...picker.querySelectorAll('input[type="radio"],select')].filter(c => c.tagName==='SELECT' ? c.name===`options[${name}]` : c.dataset.optionName===name);
    }
    selected(name) {
      const control=this.optionControls(name).find(c=>c.tagName==='SELECT'||c.checked);
      return control?.value || '';
    }
    sample(el, finish) {
      const src=this.sources[finish]; if(!src) return;
      if (el.dataset.rrPhotoSample===finish) {
        // Reobserve an unpainted chip if Shopify reconnects this component.
        if (this.sampleObserver && !el.style.getPropertyValue('background-image')) this.sampleObserver.observe(el);
        return;
      }
      el.dataset.rrPhotoSample=finish;
      if (this.sampleObserver) this.sampleObserver.observe(el);
      else this.loadSample(el, finish);
    }
    loadSample(el, finish) {
      const src=this.sources[finish]; if(!src) return;
      texture(src).then(t=>{if(t && el.isConnected && el.dataset.rrPhotoSample===finish){set(el,'background-image',`url("${t.crop}")`,'important');set(el,'background-size','cover','important');set(el,'background-position','center','important');}});
    }
    nativeSamples() {
      for (const name of this.a.optionNames) {
        for (const control of this.optionControls(name)) {
          if(control.tagName==='INPUT') {
            if(!this.sources[control.value]) continue;
            const label=[...this.picker().querySelectorAll('label[for]')].find(l=>l.htmlFor===control.id); if(!label) continue;
            let chip=label.querySelector('.rr-photo-finish-chip');
            if(!chip) {chip=document.createElement('span');chip.className='rr-photo-finish-chip';chip.setAttribute('aria-hidden','true');label.prepend(chip);label.classList.add('rr-photo-finish-label');}
            this.sample(chip,control.value);
          } else {
            let group=this.querySelector(`[data-rr-photo-option="${CSS.escape(name)}"]`);
            if(!group) {
              group=document.createElement('div');group.className='rr-photo-finish-group';group.dataset.rrPhotoOption=name;
              group.setAttribute('role','group');group.setAttribute('aria-label',name+' photographed samples');
              const title=document.createElement('p');title.className='rr-photo-finish-group__title';title.textContent=name+' samples';group.append(title);
              this.insertBefore(group,this.querySelector('.rr-photo-finish-note'));
            }
            const values=[...control.options].filter(o=>this.sources[o.value]);
            const signature=values.map(o=>o.value).join('|');
            if(group.dataset.values!==signature) {
              group.querySelectorAll('button').forEach(b=>b.remove());group.dataset.values=signature;
              values.forEach(option=>{
                const b=document.createElement('button');b.type='button';b.className='rr-photo-finish-choice';b.dataset.value=option.value;
                const chip=document.createElement('span');chip.className='rr-photo-finish-chip';chip.setAttribute('aria-hidden','true');
                const caption=document.createElement('span');caption.textContent=option.value;b.append(chip,caption);
                b.addEventListener('click',()=>{const native=this.optionControls(name).find(c=>c.tagName==='SELECT');if(native && [...native.options].some(o=>o.value===b.dataset.value&&!o.disabled)){native.value=b.dataset.value;native.dispatchEvent(new Event('change',{bubbles:true}));native.dispatchEvent(new Event('input',{bubbles:true}));}});
                group.append(b);this.sample(chip,option.value);
              });
            }
            group.querySelectorAll('button').forEach(b=>{const o=[...control.options].find(o=>o.value===b.dataset.value);b.disabled=!o||o.disabled;b.setAttribute('aria-pressed',String(control.value===b.dataset.value));});
          }
        }
      }
    }
    previewSamples(root) {
      if(!this.a.finish_button_selector) return;
      const allowed = new Set(this.a.optionNames.flatMap(name=>this.optionControls(name).flatMap(c=>c.tagName==='SELECT'?[...c.options].map(o=>o.value):[c.value])));
      root.querySelectorAll(this.a.finish_button_selector).forEach(b=>{
        const finish=[...b.attributes].find(a=>/^data-.*-finish$/.test(a.name))?.value;
        if(this.a.key==='t6' && allowed.has(finish)) {
          b.setAttribute('aria-pressed',String(this.selected(this.a.optionNames[0])===finish));
          if(!b.dataset.rrPhotoBound){b.dataset.rrPhotoBound='true';b.addEventListener('click',()=>{
            const c=this.optionControls(this.a.optionNames[0]).find(c=>c.tagName==='SELECT'?[...c.options].some(o=>o.value===finish&&!o.disabled):c.value===finish&&!c.disabled);
            if(c){if(c.tagName==='SELECT')c.value=finish;else c.checked=true;c.dispatchEvent(new Event('change',{bubbles:true}));c.dispatchEvent(new Event('input',{bubbles:true}));}
          });}
        }
        if(!allowed.has(finish)||!this.sources[finish]) return;
        const chip=b.querySelector('[class*="swatch"]'); if(chip)this.sample(chip,finish);
      });
    }
    save(el) {
      if(!this.original.has(el)) this.original.set(el,new Map(['background-image','background-size','background-repeat','background-position','background-clip','-webkit-background-clip','color','-webkit-text-fill-color','fill'].map(k=>[k,[el.style.getPropertyValue(k),el.style.getPropertyPriority(k)]])));
    }
    restore() {
      for(const [el,props] of this.original)for(const [k,[v,p]] of props){if(v)set(el,k,v,p);else el.style.removeProperty(k);}
      this.original.clear();
    }
    clearPreview(root = this.previewRoot) {
      this.restore();
      for(const [img,state]of this.imageStates||[]){if(img.getAttribute('src')===state.output){img.setAttribute('src',state.original);if(state.originalSrcset===null)img.removeAttribute('srcset');else img.setAttribute('srcset',state.originalSrcset);}}
      this.imageStates?.clear();
      if(root){delete root.dataset.rrPhotoP15;delete root.dataset.rrPhotoFinish;root.style.removeProperty('--rr-photo-p15-image');root.style.removeProperty('--rr-photo-p15-size');root.dataset.rrPhotoPaint='unavailable';}
    }
    isNegative(el, style) {
      const selectors=this.a.negative_text_selectors||[];
      if(selectors.some(s=>el.matches(s)||el.closest(s))) return true;
      return white(style.color)||white(style.webkitTextFillColor)||white(style.backgroundColor);
    }
    paint(el,t,source,stage,isText=false) {
      const s=getComputedStyle(el);if(isText&&this.isNegative(el,s))return;
      this.save(el);
      if(isText){set(el,'background-clip','text','important');set(el,'-webkit-background-clip','text','important');set(el,'color','transparent','important');set(el,'-webkit-text-fill-color','transparent','important');}
      const rect=el.getBoundingClientRect(), sr=stage.getBoundingClientRect();
      const sx=el.offsetWidth?Math.max(.05,rect.width/el.offsetWidth):1,sy=el.offsetHeight?Math.max(.05,rect.height/el.offsetHeight):1;
      const width=source.tile?sr.width*2/5:sr.width;
      const height=source.tile?width*t.h/t.w:sr.height;
      set(el,'background-image',`url("${source.tile?t.tile:t.crop}")`,'important');
      set(el,'background-size',`${width/sx}px ${height/sy}px`,'important');
      set(el,'background-repeat',source.tile?'repeat':'no-repeat','important');
      set(el,'background-position',`${(sr.left-rect.left)/sx}px ${(sr.top-rect.top)/sy}px`,'important');
    }
    paintCSS(root,stage,t,source) {
      const targets=new Set();
      for(const el of stage.querySelectorAll('*')) {
        if(el.namespaceURI===NS||el.tagName==='IMG')continue;
        const s=getComputedStyle(el);
        if((s.maskImage&&s.maskImage!=='none')||(s.webkitMaskImage&&s.webkitMaskImage!=='none'))targets.add(el);
      }
      targets.forEach(el=>this.paint(el,t,source,stage));
      const positives=(this.a.positive_text_selectors||[]).filter(s=>!s.includes('::'));
      for(const selector of positives) root.querySelectorAll(selector).forEach(el=>{
        if(el.querySelector('.rr-engine__arched-character')){
          if(this.isNegative(el,getComputedStyle(el)))return;
          this.save(el);set(el,'background-image','none','important');set(el,'color','transparent','important');set(el,'-webkit-text-fill-color','transparent','important');
          el.querySelectorAll('.rr-engine__arched-character').forEach(g=>this.paint(g,t,source,stage,true));
        }
        else this.paint(el,t,source,stage,true);
      });
      // P15's fixed EST. text is a pseudo-element; only its paint is adapted.
      if(this.a.key==='p15') {
        root.dataset.rrPhotoP15='true';
        set(root,'--rr-photo-p15-image',`url("${source.tile?t.tile:t.crop}")`);
        set(root,'--rr-photo-p15-size',source.tile?`${stage.clientWidth*2/5}px ${stage.clientWidth*2/5*t.h/t.w}px`:`${stage.clientWidth}px ${stage.clientHeight}px`);
      }
      root.dataset.rrPhotoPaint='ready';
    }
    paintSVG(root,t,source) {
      const svg=root.querySelector(this.a.svg_selector);if(!svg)return;
      const box=svg.viewBox.baseVal;if(!box.width||!box.height)return;
      let defs=svg.querySelector('defs');if(!defs){defs=document.createElementNS(NS,'defs');svg.prepend(defs);}
      let pattern=defs.querySelector('[data-rr-photo-pattern]');
      if(!pattern){pattern=document.createElementNS(NS,'pattern');pattern.id=this.paintId;pattern.dataset.rrPhotoPattern='true';pattern.setAttribute('patternUnits','userSpaceOnUse');pattern.append(document.createElementNS(NS,'image'));defs.append(pattern);}
      if(pattern.id!==this.paintId)pattern.id=this.paintId;
      const w=source.tile?box.width*2/5:box.width,h=source.tile?w*t.h/t.w:box.height;
      const attrs={x:box.x,y:box.y,width:w,height:h};for(const [k,v]of Object.entries(attrs))if(pattern.getAttribute(k)!==String(v))pattern.setAttribute(k,v);
      const img=pattern.firstElementChild;for(const [k,v]of Object.entries({width:w,height:h,preserveAspectRatio:'none',href:source.tile?t.tile:t.crop}))if(img.getAttribute(k)!==String(v))img.setAttribute(k,v);
      this.a.svg_paint_selectors.forEach(selector=>root.querySelectorAll(selector).forEach(el=>{this.save(el);set(el,'fill',`url("#${this.paintId}")`,'important');}));
      root.dataset.rrPhotoPaint='ready';
    }
    async paintImages(root,stage,t,source,finish,request) {
      for(const img of root.querySelectorAll(this.a.image_adapter_selector)) {
        const actual=img.getAttribute('src');let state=this.imageStates.get(img);
        if(!state||actual!==state.output&&actual!==state.original){state={original:actual,originalSrcset:img.getAttribute('srcset'),output:null};this.imageStates.set(img,state);}
        const filename=state.original.split('?')[0].split('/').pop();
        const mode=this.a.image_sources[filename];if(!mode)continue;
        const sizeKey=finish+'|'+state.original;
        if(state.key===sizeKey&&actual===state.output){if(img.getAttribute('srcset')!==null)img.removeAttribute('srcset');continue;}
        const mask=await sourceMask(state.original,mode);
        if(!this.isConnected||request!==this.request)return;
        if(!mask){img.dataset.rrPhotoImage='unavailable';continue;}
        const paint=new Image();paint.src=source.tile?t.tile:t.crop;await paint.decode();
        if(!this.isConnected||request!==this.request)return;
        const canvas=document.createElement('canvas');canvas.width=mask.width;canvas.height=mask.height;
        const ctx=canvas.getContext('2d');
        if(source.tile){
          const w=canvas.width*2/5,h=w*t.h/t.w;
          for(let x=0;x<canvas.width;x+=w)for(let y=0;y<canvas.height;y+=h)ctx.drawImage(paint,x,y,w,h);
        }else ctx.drawImage(paint,0,0,canvas.width,canvas.height);
        ctx.globalCompositeOperation='destination-in';ctx.drawImage(mask,0,0);
        if(mode!=='alpha'){ctx.globalCompositeOperation='destination-over';ctx.fillStyle='#fff';ctx.fillRect(0,0,canvas.width,canvas.height);}
        state.key=sizeKey;state.output=canvas.toDataURL();if(img.getAttribute('srcset')!==null)img.removeAttribute('srcset');img.setAttribute('src',state.output);img.dataset.rrPhotoImage='ready';
      }
    }
    async refresh() {
      this.nativeSamples();
      if(!this.a.root_selector||this.a.family==='approved_d13_reference')return;
      const root=this.scope.querySelector(this.a.root_selector);if(!root)return;this.previewRoot=root;
      this.previewSamples(root);
      if(!this.a.preview_stage_selector||this.a.optionNames.length!==1)return;
      const finish=this.selected(this.a.optionNames[0]);
      const source=this.sources[finish];
      const request=++this.request;
      if(!source){this.clearPreview(root);return;}
      const t=await texture(source);if(!this.isConnected||request!==this.request)return;if(!t){this.clearPreview(root);return;}
      const stage=root.querySelector(this.a.preview_stage_selector);if(!stage||stage.getBoundingClientRect().width===0)return;
      if(this.a.svg_selector)this.paintSVG(root,t,source);else this.paintCSS(root,stage,t,source);
      if(this.a.image_adapter_selector)await this.paintImages(root,stage,t,source,finish,request);
      root.dataset.rrPhotoFinish=finish;
    }
    disconnectedCallback() {
      this.scope?.removeEventListener('change',this.schedule);this.scope?.removeEventListener('input',this.schedule);this.scope?.removeEventListener('click',this.schedule);
      this.observer?.disconnect();this.resize?.disconnect();this.sampleObserver?.disconnect();this.clearPreview();this.request++;this.started=false;
    }
  }
  customElements.define('rr-photo-finish-materials',RRPhotoFinishMaterials);
})();
